import React from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { StaticQuery, graphql } from 'gatsby'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import { laptop, tablet, tabletL, tabletS, mobile } from '@/utils/media'
import { mq, rem } from '@/utils/helpers'
import { fonts, easing, lang } from '@/utils/preset'
import Image from '@/components/common/Image'

const Footer = props => {
  const { theme, ...others } = props
  return (
    <StaticQuery
      query={query}
      render={data => {
        const sources = [
          {
            ...data.head.childImageSharp.fluid,
            media: `(min-width: 768px)`
          },
          {
            ...data.headS.childImageSharp.fluid,
            media: `(max-width: 767px)`
          }
        ]
        const sources2 = [
          {
            ...data.headImage.childImageSharp.fluid,
            media: `(min-width: 768px)`
          },
          {
            ...data.headImageS.childImageSharp.fluid,
            media: `(max-width: 767px)`
          }
        ]
        const aspectRatio = {
          default: 100 / data.head.childImageSharp.fluid.aspectRatio,
          sm: 100 / data.headS.childImageSharp.fluid.aspectRatio
        }
        const aspectRatio2 = {
          default: 100 / data.headImage.childImageSharp.fluid.aspectRatio,
          sm: 100 / data.headImageS.childImageSharp.fluid.aspectRatio
        }
        const { intl } = lang(props.lang)
        return (
          <Root theme={props.theme} {...others}>
            <Head theme={theme}>
              <Head_BG_Image fluid={sources2} aspectRatio={aspectRatio2}/>
              <Head_BG_Wrapper>
                <Head_Bg fluid={sources} aspectRatio={aspectRatio} />
                <Head_Text><span>KONOIKE 2030 VISION</span></Head_Text>
              </Head_BG_Wrapper>
            </Head>
            <Main theme={props.theme}>
              <Button theme={props.theme} href={ intl('2030 Vision Numerical Target URL') } target="_blank">
                <Button_Content>
                  <Button_Text><span>{ intl('2030 Vision Numerical Target') }</span></Button_Text>
                  <Button_Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10.21 10.06" fill="currentColor">
                    <path d="M3.47094 5.03006L0.000937462 10.0601L10.2109 5.03006L0.000936582 5.90667e-05L3.47094 5.03006Z" fill="currentColor"/>
                  </Button_Icon>
                </Button_Content>
              </Button>
              <Divider />
              <Button theme={props.theme} href={ intl('Corporate Site URL') } target="_blank">
                <Button_Content>
                  <Button_Text><span>{ intl('Back To Corporate Site') }</span></Button_Text>
                  <Button_Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10.21 10.06" fill="currentColor">
                    <path d="M3.47094 5.03006L0.000937462 10.0601L10.2109 5.03006L0.000936582 5.90667e-05L3.47094 5.03006Z" fill="currentColor"/>
                  </Button_Icon>
                </Button_Content>
              </Button>
            </Main>
          </Root>
        )
      }}
    />
  )
}

const anim = {
  button: {
    color: (color1, color2) => keyframes({
      '50%': {
        color: color1
      },
      '100%': {
        color: color2
      },
      label: 'button-color'
    }),
    before: keyframes({
      '50%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(100%)'
      },
      label: 'button-after'
    }),
    after: keyframes({
      '0%': {
        transform: 'translateX(-100%)'
      },
      '50%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: 'translateX(0%)'
      },
      label: 'button-after'
    })
  }
}

const Root = styled.div`
  padding: ${rem(92)} 0;

  @media ${mq.and(mobile)} {
    padding: ${rem(24)} 0 ${rem(48)};
  }
`

const Head = styled.div`
  position: relative;
  z-index: 2;
  margin-top: ${rem(-92)};

  @media ${mq.and(mobile)} {
    margin-top: ${rem(-48)};
  }
`

const Head_BG_Wrapper = styled.div`
overflow: hidden;
  z-index: 2;
  position: relative;
`

const Head_Bg = styled(Image)`
  > div:first-of-type {
    padding-bottom: ${(props) => `${props.aspectRatio.default}% !important`};
    @media ${mq.and(mobile)} {
      padding-bottom: ${(props) => `${props.aspectRatio.sm}% !important`};
    }
  }
`

const Head_BG_Image = styled(Image)`
  position: relative;
  bottom: ${rem(-92)};

  @media ${mq.and(mobile)} {
    bottom: ${rem(-48)};
  }

  > div:first-of-type {
    padding-bottom: ${(props) => `${props.aspectRatio.default}% !important`};
    @media ${mq.and(mobile)} {
      padding-bottom: ${(props) => `${props.aspectRatio.sm}% !important`};
    }
  }
`

const Head_Text = styled.h3`
  position: absolute;
  top: 7vw;
  left: 50%;
  display: flex;
  justify-content: center;
  transform: translateX(-50%);
  width: 100%;

  @media ${mq.and(tabletL)} {
    top: ${rem(80)};
  }

  @media ${mq.and(tabletS)} {
    top: ${rem(64)};
  }

  @media ${mq.and(mobile)} {
    top: ${rem(36)};
  }

  span {
    text-align: center;
    font-size: ${rem(32)};
    padding-bottom: ${rem(4)};
    font-family: ${fonts.base.en};
    font-weight: bold;
    line-height: ${1};
    color: #fff;
    border-bottom: ${rem(2)} solid currentColor;

    @media ${mq.and(laptop)} {
      font-size: ${rem(36)};
    }

    @media ${mq.and(tablet)} {
      font-size: ${rem(32)};
    }

    @media ${mq.and(mobile)} {
      font-size: ${rem(30)};
    }
  }
`

const Main = styled.div`
  margin-top: ${rem(40)};

  @media ${mq.and(mobile)} {
    padding: 0 ${rem(20)};
  }
`

const Button = styled.a`
  margin: 0 auto;
  position: relative;
  display: block;
  width: ${rem(420)};
  max-width: 100%;
  height: ${rem(58)};
  overflow: hidden;
  color: ${props => props.theme.primary.exdark};
  background-color: ${props => props.theme.primary.exdark};
  border: 1px solid ${props => props.theme.primary.exdark};
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #fff;
  }
  &:after {
    transform: translateX(-100%);
  }
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      animation: ${props => anim.button.color(props.theme.primary.contrast.exdark, props.theme.primary.exdark)} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:before {
      animation: ${anim.button.before} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:after {
      animation: ${anim.button.after} 0.5s ${easing.css.easeInOutExpo};
    }
  }
`

const Button_Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: ${rem(18)};
  @media ${mq.and(mobile)} {
    font-size: ${rem(14)};
  }
`

const Button_Icon = styled.svg`
  position: absolute;
  top: 50%;
  right: ${rem(22)};
  width: ${rem(17)};
  height: ${rem(17)};
  transform: translateY(-50%);
  @media ${mq.and(tablet, mobile)} {
    right: ${rem(12)};
    width: ${rem(10.2132)};
    height: ${rem(10.06)};
  }
`

const Button_Text = styled.span`
  transform: translateY(-10%);
  font-weight: bold;
`

const Divider = styled.div`
  margin: ${rem(62)} auto;
  max-width: ${rem(1200)};
  height: ${rem(2)};
  border-top: 2px solid #000;

  @media ${mq.and(mobile)} {
    margin: ${rem(32)} auto;
  }
`

export default ThemeConsumer(Footer)

export const query = graphql`
  query FooterQuery {
    head: file(relativePath: { eq: "footer-head.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    headS: file(relativePath: { eq: "footer-head-sm.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    headImage: file(relativePath: { eq: "footer-head-image.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    headImageS: file(relativePath: { eq: "footer-head-image-sm.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`
